/**
 * SCOPE
 *   The tables read for previews, kept between reads while the document stands still.
 *
 * WHY
 *   Every table card asks for its table on every refresh, and the drawer asks for
 *   it again. Reading a table is three round trips to Word and a package of some
 *   hundred kilobytes, and the cards of one table all ask within the same two
 *   seconds. One read serves them all until a paragraph changes.
 */
import { OoxmlRange } from "@common/utils/redline/ooxml-range";

import type { TableRead, WordChange } from "../word-document";

export class TableReadCache {
    private readonly readsByBookmarkName = new Map<string, TableRead>();

    /** The table last read around `bookmarkName`, or null when none was read since the document last changed. */
    get(bookmarkName: string): TableRead | null {
        return this.readsByBookmarkName.get(bookmarkName) ?? null;
    }

    /**
     * Keeps a read under the bookmark it was asked for and under every bookmark
     * the table carries, so the other cards of the same table find it by their own.
     *
     * FALLBACK
     *   A package that fails to parse is kept under the asked bookmark alone.
     */
    store(bookmarkName: string, read: TableRead): void {
        this.readsByBookmarkName.set(bookmarkName, read);
        try {
            for (const name of new OoxmlRange(read.packageOoxml).paragraphElementsByBookmarkName().keys()) this.readsByBookmarkName.set(name, read);
        } catch {
            return;
        }
    }

    /** Forgets every read: the body was replaced, or the add-in wrote. */
    clear(): void {
        this.readsByBookmarkName.clear();
    }

    /**
     * Forgets the reads a change Word reported may have made stale.
     *
     * CASES
     *   - a paragraph was added or deleted  -> every read: a row may have come or gone, and a new paragraph's id is in no read yet
     *   - a content control was added       -> every read: the event names no paragraph, so nothing says where it landed
     *   - an event naming no paragraph      -> every read: nothing says where the change was
     *   - paragraphs changed                -> the tables holding one of them; typing elsewhere leaves every table as read
     */
    forgetParagraphs(change: WordChange): void {
        if (change.kind !== "changed" || change.uniqueLocalIds.length === 0) {
            this.clear();
            return;
        }
        for (const [bookmarkName, read] of Array.from(this.readsByBookmarkName)) {
            if (read.paragraphUniqueLocalIds.some((id) => change.uniqueLocalIds.includes(id))) this.readsByBookmarkName.delete(bookmarkName);
        }
    }
}
